import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

/**
 * Custom hook to manage and update URL search parameters.
 *
 * @returns {[URLSearchParams, Function]} A tuple containing the current search parameters
 * (`URLSearchParams`) and a function to update them.
 *
 * The `updateSearchParams` function accepts either an array of objects or an object.
 * - If an array is provided, each object in the array represents a parameter to update with the following structure:
 *   - `key` (string): The name of the search parameter.
 *   - `value` (string | undefined | null): The value of the search parameter. If the value is
 *     `undefined`, `null`, or an empty string, the parameter will be removed from the URL.
 * - If an object is provided, its keys represent parameter names, and its values represent the corresponding parameter values.
 *   If a value is `undefined`, `null`, or an empty string, the parameter will be removed from the URL.
 *
 * Example usage:
 * ```javascript
 * const [searchParams, updateSearchParams] = useUpdateSearchParams();
 *
 * // Using an array of objects
 * updateSearchParams([
 *   { key: 'page', value: '2' },
 *   { key: 'filter', value: null }, // Removes the 'filter' parameter
 * ]);
 *
 * // Using an object
 * updateSearchParams({
 *   page: '2',
 *   filter: null, // Removes the 'filter' parameter
 * });
 * ```
 */
export function useUpdateSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const updateSearchParams = useCallback(
    (params) => {
      const newParams = new URLSearchParams(searchParams)
      let hasChanges = false

      const paramArray = Array.isArray(params) ? params : Object.entries(params).map(([key, value]) => ({ key, value }))

      paramArray.forEach(({ key, value }) => {
        if (value !== undefined && value !== null && value !== '') {
          if (searchParams.get(key) !== value) {
            newParams.set(key, value)
            hasChanges = true
          }
        } else if (searchParams.has(key)) {
          newParams.delete(key)
          hasChanges = true
        }
      })

      if (hasChanges) {
        setSearchParams(newParams)
      }
    }, [searchParams])

  const result = useMemo(() => [searchParams, updateSearchParams], [searchParams])

  return result
}
